import React, { useMemo, useRef } from 'react';
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router';
import { useOnClickOutside } from 'usehooks-ts';
import ConditionalLayout from '@/components/layouts/conditional-layout';
import ROUTES from '@/routes';
import ContactIndexRoute from '@/routes/contact/index';
import cn from '@/utils/merge-class-names';

type OuterVariant = 'menu' | 'menuLeft' | 'default';
type ContentVariant = 'details' | 'remark' | 'createGroup' | 'friendRequest' | 'default';

export default function ContactLayout() {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const parentPath = ROUTES.CONTACT.$path();
  const isRootPath = location.pathname === parentPath;
  const createGroupStep = location.state?.step;

  const OUTER_CLASSNAME: Record<OuterVariant, string> = {
    menu: 'h-fit w-fit py-0 bg-transparent left-[30.5rem] top-5',
    menuLeft: 'h-fit w-fit py-0 bg-transparent left-[27.5rem] top-14',
    default: '',
  };

  const CONTENT_CLASSNAME: Record<ContentVariant, string> = {
    details: 'w-[25rem] h-[53.5rem] ',
    remark: 'w-[24.5rem] h-[25rem]',
    //因為結構和共用元件與聊天頁面Modal相似，暫時加上"group modal-layout p-4"更改app-header
    createGroup: cn(
      'group modal-layout p-4',
      createGroupStep === 0 ? 'w-[25rem] h-[52.25rem]' : 'w-[25rem] h-[37.5rem]',
    ),
    friendRequest: 'w-[25rem] h-[37.5rem]',
    default: 'w-[25rem] h-[25rem]',
  };

  const CHILDREN_PATHS: Record<string, { outer: OuterVariant; content: ContentVariant }> = {
    [ROUTES.CONTACT.USERS.APPLICATIONS.$path()]: { outer: 'menuLeft', content: 'default' },
    [ROUTES.CONTACT.USERS.BY_ID.$path()]: { outer: 'menu', content: 'details' },
    [ROUTES.CONTACT.USERS.BY_ID.GROUPS.$path()]: { outer: 'menu', content: 'details' },
    [ROUTES.CONTACT.USERS.BY_ID.REMARK.$path()]: { outer: 'menu', content: 'remark' },
    [ROUTES.CONTACT.REQUEST.$path()]: { outer: 'menu', content: 'friendRequest' },
    [ROUTES.CONTACT.REQUEST.USER.$path()]: { outer: 'menu', content: 'friendRequest' },
    [ROUTES.CONTACT.GROUPS.$path()]: { outer: 'menuLeft', content: 'default' },
    [ROUTES.CONTACT.GROUPS.CREATE.$path()]: { outer: 'menu', content: 'createGroup' },
  };

  const { outer: modalOuterVariant, content: modalContentVariant } = useMemo<{
    outer: OuterVariant;
    content: ContentVariant;
  }>(() => {
    return (
      Object.entries(CHILDREN_PATHS).find(([path]) => matchPath(path, location.pathname))?.[1] || {
        outer: 'default',
        content: 'default',
      }
    );
  }, [location.pathname]);

  const closeOnClickOutside = ['menuLeft', 'menu'].includes(modalOuterVariant);

  useOnClickOutside(ref, () => {
    if (!(document.getElementById('headlessui-portal-root') || document.getElementById('flyout-modal'))) {
      navigate(parentPath, { replace: true });
    }
  });

  return (
    <ConditionalLayout pathname={`${ROUTES.CONTACT.$path()}/*`}>
      {(isDesktop, match) =>
        isDesktop ? (
          <>
            <div className='w-[352px] h-full flex'>
              <ContactIndexRoute />
            </div>
            {match && !isRootPath && (
              <div className={cn('fixed z-40 mt-7', OUTER_CLASSNAME[modalOuterVariant])}>
                <div className='flex-col flex flex-1 items-center justify-center'>
                  <div
                    ref={closeOnClickOutside ? ref : undefined}
                    className={cn(
                      'flex bg-white rounded-2xl overflow-hidden shadow-modal relative',
                      CONTENT_CLASSNAME[modalContentVariant],
                    )}
                    onClick={(e) => e.stopPropagation()}>
                    <Outlet />
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <Outlet />
        )
      }
    </ConditionalLayout>
  );
}
